<template>
  <div class="pt-2">
    <div v-if="showTable" class="overflow-x-auto overflow-y-auto">
      <v-data-table
        :headers="headers"
        :items="amcPlans"
        class="table-radius border border-[#e0e0e0]"
        :options.sync="options"
        :footer-props="{ itemsPerPageOptions: [5, 10, 15, 30] }"
        :server-items-length="totalPlans"
        fixed-header
        show-select
        :loading="loadTable"
      >
        <template v-for="(header, index) in headers" v-slot:[`header.${header.value}`]>
          <p
            :key="index"
            class="font-title"
            v-if="header.text?.length > 15"
            v-tooltip="{ text: header.text }"
          >
            {{ header.text }}
          </p>
          <p :key="index" v-else class="font-title">{{ header.text }}</p>
        </template>
        <template v-slot:item.planName="{ item }">
          <td class="px-4 py-6 table-font">{{ item.planName }}</td>
        </template>
        <template v-slot:item.planDuration="{ item }">
          <td class="px-4 table-font">{{ item.planDuration }} days</td>
        </template>
        <template v-slot:item.coverage="{ item }">
          <td class="px-4 table-font">
            {{ item.coverage && item.coverage.length > 0 ? item.coverage[0] : '' }}
          </td>
        </template>
        <template v-slot:item.serviceSchedule="{ item }">
          <td class="px-4 table-font">{{ item.serviceSchedule }}</td>
        </template>
        <template v-slot:item.planAdded="{ item }">
          <td class="px-4 table-font">{{ item.planAdded }}</td>
        </template>
        <template v-slot:item.payment="{ item }">
          <td class="px-4 table-font">{{ item.finalPrice }}</td>
        </template>
        <template v-slot:item.paymentSchedule="{ item }">
          <td class="px-4 table-font">{{ item.paymentSchedule }}</td>
        </template>
        <template v-slot:item.status="{ item }">
          <td class="px-4 pt-6 table-font">
            <v-select
              solo
              flat
              dense
              :menu-props="{ offsetY: true }"
              v-model="item.status"
              :items="statusOptions"
              item-text="text"
              item-value="value"
            ></v-select>
          </td>
        </template>
        <template v-slot:item.actions="{ item }">
          <td class="px-4">
            <action-button
              :item="item"
              :canEdit="canEdit"
              :canDelete="canDelete"
              :showDeleteButton="canDelete"
              :showEditButton="canEdit"
              :handleEdit="() => editItem(item)"
              :handleDelete="() => deleteItem(item)"
            />
          </td>
        </template>
      </v-data-table>
    </div>
    <div v-else-if="showAddAmcList">
      <AddAmcList @cancel="toggleShowTable" />
    </div>

    <div v-else-if="showAddAmcPlan">
      <AddAmcPlan @cancel="toggleShowTable" :editedItem="editedItem" />
    </div>
  </div>
</template>

<script>
import ActionButton from '../../ActionButton.vue'
import AddAmcList from './AddAmcList.vue'
import AddAmcPlan from './AddAmcPlan.vue'
import { debounce, hasPermission } from '@/utils/common'
import permissionData from '@/utils/permissions'

export default {
  name: 'AmcList',
  components: {
    // AddAmcList,
    // AddAmcPlan,
    ActionButton
  },
  props: {
    search: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      userData: this.$store.state.wfmPermissions,
      loadTable: true,
      statusOptions: [
        { text: 'Active', value: 'ACTIVE' },
        { text: 'In Active', value: 'INACTIVE' }
      ],
      options: { sortBy: [], sortDesc: [], itemsPerPage: 5, page: 1 },
      amcPlans: [],
      totalPlans: 0,
      editedItem: null,
      filterName: '',
      showTable: true,
      showAddAmcList: false,
      showAddAmcPlan: false
    }
  },
  watch: {
    options: {
      handler() {
        this.getAmcPlans()
      },
      deep: true
    },
    search: {
      handler() {
        this.options.page = 1
        this.getAmcPlans()
      }
    }
  },
  async mounted() {
    this.getAmcPlans()
    // Listen for refresh event
    this.$root.$on('refresh-amc-plans', this.getAmcPlans)
  },
  beforeDestroy() {
    // Remove event listener to prevent memory leaks
    this.$root.$off('refresh-amc-plans', this.getAmcPlans)
  },
  computed: {
    canEdit() {
      return hasPermission(this.userData, permissionData.amcEdit)
    },
    canDelete() {
      return hasPermission(this.userData, permissionData.amcDelete)
    },
    canWrite() {
      return hasPermission(this.userData, permissionData.amcWrite)
    },
    headers() {
      const action = {
        text: 'Action',
        value: 'actions',
        width: '16%',
        sortable: false
      }

      const headers = [
        { text: 'Plan Name', value: 'planName', width: '15%', sortable: false },
        {
          text: 'Plan Duration',
          value: 'planDuration',
          width: '10%',
          sortable: false
        },
        { text: 'Coverage', value: 'coverage', width: '25%', sortable: false },
        {
          text: 'Servicing Schedule',
          value: 'serviceSchedule',
          width: '13%',
          sortable: false
        },
        { text: 'Plan Price', value: 'payment', width: '10%', sortable: false },
        {
          text: 'Payment Scheduled',
          value: 'paymentSchedule',
          width: '14%',
          sortable: false
        },
        { text: 'Plan Status', value: 'status', width: '16%', sortable: false },
        ...(this.canEdit || this.canDelete ? [action] : [])
      ]
      return headers
    }
  },
  methods: {
    toggleShowTable() {
      this.showAddAmcPlan = false
      this.showAddAmcList = false
      this.showTable = true
      this.editedItem = {}
      this.getAmcPlans()
    },
    // toggleShowAddAmcList() {
    //   this.showTable = false
    //   this.showAddAmcPlan = false
    //   this.showAddAmcList = true
    // },
    // toggleShowAddAmcPlan() {
    //   this.showTable = false
    //   this.showAddAmcList = false
    //   this.showAddAmcPlan = true
    // },
    async getAmcPlans() {
      try {
        const { page, itemsPerPage } = this.options
        const params = {
          page: page,
          limit: itemsPerPage > -1 ? itemsPerPage : this.totalPlans
        }
        if (this.search) {
          params.search = this.search
        }
        this.loadTable = true
        const response = await this.$axios.get('/ecom/amcplans', {
          params: params
        })
        this.loadTable = false
        this.amcPlans = response.data?.plans
        this.totalPlans = response.data.pagination.totalCount
      } catch (error) {
        console.log(error)
        this.loadTable = false
      }
    },
    editItem(item) {
      this.$emit('edit-item', item)
    },
    async deleteItem(item) {
      try {
        const response = await this.$axios.delete(`/ecom/amcplan/${item._id}`)
        this.getAmcPlans()
        const successMessage = response?.data?.message || 'Plans deleted successfully'
        this.$toast.success(successMessage)
        this.$emit('delete-item', item)
      } catch (error) {
        console.error('Error deleting item:', error)
        const errorMessage = error.response.data.message || error
        this.$toast.error(errorMessage)
      }
    },
    handleInput: debounce(function () {
      this.options.page = 1
      this.getAmcPlans()
    }, 1000)
  }
}
</script>
<style scoped>
.v-data-table >>> .v-data-table__wrapper > table > tbody > tr > td > .v-data-table__checkbox {
  text-align: center;
}
.table-radius {
  border-radius: 0px !important;
}
.font-title {
  font-size: 0.875rem;
  color: #000;
  margin-bottom: 0px !important;
  max-width: 120px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
